import {announce} from '@react-aria/live-announcer'
import {Heading} from 'react-aria-components'

import CloseSvg from '@/assets/icons/close.svg?react'
import {type ChatSectionControl} from '@/components/ChatSection/ChatSection'
import useMessages from '@/components/ChatSection/hooks/useMessages'
import {messageInputAtom, messageInputIsDisabledAtom} from '@/components/ChatSection/states'
import type {Thread} from '@/models/threadModel'

import style from './MiniChat.module.scss'

const ChatSection = lazy(async () => import('@/components/ChatSection/ChatSection'))

export interface MiniChatControl {
  startNewChat: MemoizedCallback<() => void>
  selectThread: MemoizedCallback<(threadId: Thread['id']) => void>
  submitMessage: MemoizedCallback<(e: string | React.FormEvent, isRegenerate?: boolean) => void>
  setVariationThreadId: MemoizedCallbackOrDispatch<string | undefined>
}

interface Props {
  ref?: Ref<MiniChatControl>
  onClose?: () => void
}

export default deepMemo(function MiniChat({onClose, ref, children}: PropsWithChildren<Props>) {
  const store = useStore()
  const chatSectionRef = useRef<ChatSectionControl>(null)

  const clearMessageInput = useCallback(() => {
    store.set(messageInputAtom, '')
  }, [store])

  const disableMessageInput = useCallback(
    (isDisabled?: boolean) => {
      store.set(messageInputIsDisabledAtom, !!isDisabled)
    },
    [store],
  )

  const {
    submitMessage,
    isGenerating,
    stopGenerate,
    setActiveThreadId,
    activeThreadId,
    setVariationThreadId,
  } = useMessages({
    // TODO: consider change this behavior
    mini: true,
    // After submitMessage successfully
    onStartGenerating: useCallback(() => {
      announce('One moment, Innosearch is generating the response...', 'assertive')
      clearMessageInput()
      disableMessageInput()
      chatSectionRef.current?.scrollToBottomChat()
    }, [clearMessageInput, disableMessageInput]),
    // After submitMessage done (isActive = false), or stop by user (isActive = true)
    onStopGenerating: useCallback(
      (isActive: boolean) => {
        disableMessageInput(false)
        if (!isActive) {
          chatSectionRef.current?.scrollToBottomChat()
        }
      },
      [disableMessageInput],
    ),
    // TODO: fix bug when send the first message of the thread, q got clear (because after sending the first message of the thread, message will be reload from remote server - by react-query)
    onStartLoadingMessages: useCallback(() => {
      clearMessageInput()
      disableMessageInput(false)
    }, [clearMessageInput, disableMessageInput]),
    onStopLoadingMessages: useCallback(() => {
      chatSectionRef.current?.scrollToBottomChat()
    }, []),
  })

  const startNewChat = useCallback(() => {
    clearMessageInput()
    stopGenerate(Symbol('nothing'), true, 'new chat')
    setActiveThreadId(undefined)
    chatSectionRef.current?.focusToMessageInput()
  }, [clearMessageInput, setActiveThreadId, stopGenerate])

  const selectThread = useCallback(
    (threadId: Thread['id']) => {
      stopGenerate(Symbol('nothing'), true, 'change thread')
      setActiveThreadId(threadId)
      chatSectionRef.current?.focusToMessageInput()
    },
    [setActiveThreadId, stopGenerate],
  )

  useImperativeHandle(ref, () => {
    return {
      startNewChat,
      selectThread,
      submitMessage,
      setVariationThreadId,
    } satisfies MiniChatControl
  }, [selectThread, setVariationThreadId, startNewChat, submitMessage])

  return (
    <div className={style.MiniChat}>
      <div className={style.TopBar}>
        <Heading slot='title' level={2}>
          Chat with Innosearch
        </Heading>
        <button className={style.CloseBtn} onClick={onClose} aria-label='close'>
          <CloseSvg aria-hidden />
        </button>
      </div>
      <Suspense>
        <ChatSection
          ref={chatSectionRef}
          activeThreadId={activeThreadId}
          isGenerating={isGenerating}
          submitMessage={submitMessage}
          stopGenerate={stopGenerate}
        >
          {children}
        </ChatSection>
      </Suspense>
    </div>
  )
})
